import { createRef, ReactElement, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { useFormContext } from 'react-hook-form';

import isEqual from 'fast-deep-equal';

import { useLoadingStore } from '@hsp/react-component-library';
import { isDefined, isNotDefined, isOfType } from '@hsp/utilities';

import { useAppSelector } from '@h/store';

import { selectAudienceAggregateApplied, selectAudienceAggregateTotalDonors } from '@s/selectors';

import { useActions } from 'hooks/actions';
import { useNavigateTo } from 'hooks/navigate';
import { useCategory, useGenerateSearchParameters } from 'pages/audience/shared/hooks';
import {
  Client,
  Description,
  GiftAmount,
  MailCode,
  Name,
  Order,
  OrderType,
  Selects,
  Tags,
} from 'pages/audience/shared/save/components/slideout/components';
import {
  useIsExisting,
  useSaveMasterFile,
  useSavePreBuilt,
  useSaveShop,
  useUpdateAudience,
  useUpdateMasterFile,
  useUpdatePreBuilt,
  useUpdateUserAudience,
} from 'pages/audience/shared/save/hooks';
import { useSaveStoreSelector, useSaveStoreSet } from 'pages/audience/shared/save/hooks/context';
import {
  AudienceStatus,
  defaultSearch,
  IAudience,
  IAudienceSaveResult,
  IAudienceSearchApi,
  IAudienceShop,
  MultiRfmSearchApi,
} from 'types/api/audience';
import { IResult } from 'types/api/common';
import { ISaveProps, SaveFeatures, SaveForm } from 'types/pages/audience-builder/shared/save';
import { notifications } from 'utilities/notifications';
import { getSortString } from 'utilities/sorts';
import { buildUrl, s } from 'utilities/strings';

import { useGetDisplayName } from './display-name.hook';

export const useConfiguration = ({
  onClose,
  onSaved,
  changeStatus,
}: Pick<ISaveProps, 'onClose' | 'onSaved' | 'changeStatus'>) => {
  const isApplied = useAppSelector(selectAudienceAggregateApplied);
  const totalDonors = useAppSelector(selectAudienceAggregateTotalDonors);

  const run = useRef<boolean>(false);

  const data = useSaveStoreSelector(({ data }) => data);
  const features = useSaveStoreSelector(({ features }) => features);
  const isOpen = useSaveStoreSelector(({ isOpen }) => isOpen);
  const setSaveStore = useSaveStoreSet();

  const { add, cleanup } = useActions();
  const { categories } = useCategory();
  const name = useGetDisplayName();
  const { handleSubmit, reset } = useFormContext<SaveForm>();
  const generateSearchParameters = useGenerateSearchParameters();
  const isExisting = useIsExisting();
  const setLoading = useLoadingStore(({ setIsLoading }) => setIsLoading);
  const location = useLocation();
  const navigate = useNavigateTo();
  const { mutateAsync: saveShop } = useSaveShop();
  const { mutateAsync: saveMasterFile } = useSaveMasterFile();
  const { mutateAsync: savePreBuilt } = useSavePreBuilt();
  const { mutateAsync: updateAudience } = useUpdateAudience();
  const { mutateAsync: updateMasterFile } = useUpdateMasterFile();
  const { mutateAsync: updatePreBuilt } = useUpdatePreBuilt();
  const { mutateAsync: updateShop } = useUpdateUserAudience();

  const formRef = createRef<HTMLFormElement>();

  const components = useMemo(() => {
    const children: ReactElement[] = [];

    if (features & SaveFeatures.OrderType) {
      children.push(<OrderType />);
    }

    if (features & SaveFeatures.AudienceName) {
      children.push(<Name />);
    }

    if (features & SaveFeatures.Client) {
      children.push(<Client />);
    }

    if (features & SaveFeatures.GiftAmount) {
      children.push(<GiftAmount />);
    }

    if (features & SaveFeatures.Order) {
      children.push(<Order />);
    }

    if (features & SaveFeatures.Mailcode) {
      children.push(<MailCode />);
    }

    if (features & SaveFeatures.Tags) {
      children.push(<Tags />);
    }

    if (features & SaveFeatures.Description) {
      children.push(<Description />);
    }

    if (features & SaveFeatures.Selects) {
      children.push(<Selects />);
    }

    return children;
  }, [features]);

  const handleClosed = useCallback(() => {
    onClose?.();
    reset();
    setSaveStore({ isOpen: false, saveAsMaster: false, saveAsPreBuilt: false, saveTo: false });
  }, [onClose, reset, setSaveStore]);

  const handleError = useCallback((_: unknown) => {
    // do nothing
  }, []);

  const handleFormSubmit = useCallback(() => formRef.current?.requestSubmit(), [formRef]);

  const handleSubmittedData = useCallback(
    (formData: SaveForm) => {
      const { type } = formData;
      let promise: Promise<IResult<IAudience | IAudienceSaveResult> | undefined> | null = null;

      setLoading(true);

      if (type === 'audience' || type === 'audience-master-file' || type === 'audience-pre-built') {
        promise = updateAudience(formData);
      } else {
        let autoTag: string[] = [];
        let parameters: IAudienceSearchApi | MultiRfmSearchApi | undefined;

        if (!isOfType<IAudienceShop>(data, 'categoryId')) {
          return;
        }

        const emptySearch = defaultSearch();
        const searchParameters = generateSearchParameters(categories);

        if (isEqual(searchParameters, emptySearch)) {
          parameters = data.parameters;
        } else {
          parameters = {
            ...searchParameters,
            sort: getSortString(searchParameters?.sort),
          };

          if (isOfType<MultiRfmSearchApi>(parameters, 'rfmSets')) {
            const { rfmSets } = parameters;

            for (const rfmSet of rfmSets) {
              const { autoTag: rfmSetTags } = rfmSet;

              for (const tag of rfmSetTags ?? []) {
                autoTag.push(tag);
              }
            }
          } else {
            const { autoTag: tags } = parameters;

            if (isDefined(tags)) {
              autoTag = tags;
            }
          }
        }

        if (isDefined(parameters)) {
          formData = { autoTag, parameters, ...formData };
          switch (type) {
            case 'basic':
              if (isDefined(changeStatus) && changeStatus) {
                formData.statusId = AudienceStatus.Active;
                formData.quantity = isApplied ? totalDonors : 0;
              }
              promise = isExisting ? updateShop(formData) : saveShop(formData);
              break;

            case 'master-file':
              promise = isExisting ? updateMasterFile(formData) : saveMasterFile(formData);
              break;

            case 'pre-built':
              promise = isExisting ? updatePreBuilt(formData) : savePreBuilt(formData);
              break;
          }
        }
      }

      promise
        ?.then((result) => {
          if (isNotDefined(result)) {
            throw new Error(s(['Api.UnknownError']));
          }

          const { dataContainer, success } = result;

          if (success && isDefined(dataContainer)) {
            notifications.success(s('Site.Pages.AudienceBuilder.Dashboard.Messages.Status.Update.Success'));

            onSaved?.(dataContainer);

            setSaveStore({ isOpen: false });

            if (run.current) {
              if (isOfType<IAudience>(dataContainer, 'category')) {
                return;
              }

              navigate(buildUrl('Root.children.AudienceBuilder.children.Results.children.id'), {
                options: {
                  state: { from: location },
                },
                params: { id: dataContainer.shopId },
              });
            }
          } else {
            notifications.error(s('Site.Pages.AudienceBuilder.Dashboard.Messages.Status.Update.Error'));
          }
        })
        .catch(() => notifications.error(s('Site.Error')))
        .finally(() => setLoading(false));
    },
    [
      categories,
      changeStatus,
      data,
      generateSearchParameters,
      isApplied,
      isExisting,
      location,
      navigate,
      onSaved,
      saveMasterFile,
      savePreBuilt,
      saveShop,
      setLoading,
      setSaveStore,
      totalDonors,
      updateAudience,
      updateMasterFile,
      updatePreBuilt,
      updateShop,
    ]
  );

  useEffect(() => {
    add('save', handleFormSubmit);
    add('save-and-run', () => {
      run.current = true;
      handleFormSubmit();
    });

    return () => cleanup('save', 'save-and-run');
  }, [add, cleanup, handleFormSubmit]);

  return {
    components,
    formRef,
    handleClosed,
    handleError,
    handleSubmit,
    handleSubmittedData,
    isOpen,
    name,
  };
};
